import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ReconcileState } from '@treadinc/horizon-api-spec';
import { useCallback, useEffect, useRef } from 'react';

import useSettlementLines from '~hooks/useSettlementLines/useSettlementLines';
import { Settlement } from '~hooks/useSettlements/models';

const SETTLEMENT_LINES_QUERY_KEY = 'settlementLines';

interface UseSettlementLinesQueryProps {
  companyId: string;
  reconcileState: Settlement['reconcileState'];
  settlementId: string;
}

export default function useSettlementLinesQuery({
  companyId,
  reconcileState,
  settlementId,
}: UseSettlementLinesQueryProps) {
  const { getSettlementAddOnCharges, getSettlementInvoiceLines } = useSettlementLines();
  const queryClient = useQueryClient();
  const { data, isError, isFetching, refetch } = useQuery({
    queryKey: [SETTLEMENT_LINES_QUERY_KEY, companyId, settlementId],
    queryFn: async ({ signal }) => {
      const [invoiceLines, settlementAddOnCharges] = await Promise.all([
        getSettlementInvoiceLines(companyId, settlementId, signal),
        getSettlementAddOnCharges(companyId, settlementId, signal),
      ]);

      return { invoiceLines, settlementAddOnCharges };
    },
    retry: false,
  });
  const rereadLines = useCallback(async () => {
    await queryClient.cancelQueries({
      queryKey: [SETTLEMENT_LINES_QUERY_KEY, companyId, settlementId],
    });
    await refetch();
  }, [companyId, queryClient, refetch, settlementId]);
  const previousReconcileStateRef = useRef(reconcileState);

  useEffect(() => {
    const previousReconcileState = previousReconcileStateRef.current;
    previousReconcileStateRef.current = reconcileState;

    if (
      previousReconcileState !== ReconcileState.COMPLETED &&
      reconcileState === ReconcileState.COMPLETED
    ) {
      void rereadLines();
    }
  }, [reconcileState, rereadLines]);

  return { data, isError, isFetching, refetch: rereadLines };
}
